import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

// A single, app-wide "this is a demo" notice. Any control that would write to
// the database in the live product calls `showDemo(...)` instead of hitting the
// API — the user sees an explanation of what the action would do, and nothing
// is persisted.

export interface DemoNotice {
  title: string;
  body: ReactNode;
}

interface DemoCtx {
  showDemo: (notice: DemoNotice) => void;
}

const Ctx = createContext<DemoCtx | null>(null);

export function useDemo(): DemoCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDemo must be used within <DemoProvider>');
  return ctx;
}

export function DemoProvider({ children }: { children: ReactNode }) {
  const [notice, setNotice] = useState<DemoNotice | null>(null);
  const showDemo = useCallback((n: DemoNotice) => setNotice(n), []);

  return (
    <Ctx.Provider value={{ showDemo }}>
      {children}
      {notice && (
        <div className="modal-backdrop" onClick={() => setNotice(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">{notice.title}</div>
            <div className="modal-body">
              <div style={{ fontSize: 14, lineHeight: 1.5 }}>{notice.body}</div>
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                This is an interactive demo — no data is changed.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setNotice(null)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

// Centralized copy for the fixed (non-parameterized) demo notices. Parameterized
// ones (with yard/material/quantity) are built inline at the call site.
export const DEMO = {
  chooseFile: {
    title: 'Choose a file',
    body: (
      <>
        In the live application this opens your file browser to pick a CSV of past
        delivery records. The demo runs on a fixed sample dataset, so uploading your
        own file is disabled here.
      </>
    ),
  },
  importRecords: {
    title: 'Import delivery records',
    body: (
      <>
        In the live app this uploads your CSV, inserts each delivery record, re-fits
        the freight curve, and recomputes delivered pricing across every lane.
      </>
    ),
  },
  submitPrices: {
    title: 'Submit price updates',
    body: (
      <>
        In the live app this saves your edited prices to the database and immediately
        recomputes delivered pricing and cheapest-yard sourcing.
      </>
    ),
  },
  saveSite: {
    title: 'Save site',
    body: (
      <>
        In the live app this adds or updates the site record, geocodes its address,
        and refreshes sourcing for that location.
      </>
    ),
  },
  deleteSite: {
    title: 'Delete site',
    body: <>In the live app this permanently removes the site and its sourcing history.</>,
  },
  saveYard: {
    title: 'Save yard',
    body: (
      <>
        In the live app this adds or updates the yard, geocodes its address, and
        recomputes lane distances and pricing for everything it stocks.
      </>
    ),
  },
  deleteYard: {
    title: 'Delete yard',
    body: <>In the live app this permanently removes the yard and its price records.</>,
  },
  updateOrderStatus: {
    title: 'Update order status',
    body: <>In the live app this changes the order's status and notifies the crew assigned to it.</>,
  },
  saveOrder: {
    title: 'Save delivery order',
    body: (
      <>
        In the live app this creates the delivery order in the system, assigns an order
        number, and decrements available inventory at the yard.
      </>
    ),
  },
} satisfies Record<string, DemoNotice>;
